import { useRef, useState, useEffect, useCallback } from "react";

interface Props {
  before: string;
  after: string;
  beforeAlt?: string;
  afterAlt?: string;
  className?: string;
  rounded?: string;
  initial?: number;
}

/**
 * Premium drag-to-compare before/after slider.
 * Independent of ImageComparisonSlider (kept for the rest of the app).
 * Works with mouse + touch, respects reduced motion for the intro sweep.
 */
export const BeforeAfterCompare = ({
  before,
  after,
  beforeAlt = "Before",
  afterAlt = "After",
  className = "",
  rounded = "rounded-[24px]",
  initial = 55,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(initial);
  const dragging = useRef(false);

  const move = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => dragging.current && move(e.clientX);
    const onTouch = (e: TouchEvent) => dragging.current && move(e.touches[0].clientX);
    const stop = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", onTouch, { passive: true });
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("touchend", stop);
    };
  }, [move]);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden select-none group ${rounded} ${className}`}
    >
      <img
        src={after}
        alt={afterAlt}
        className="w-full h-full object-cover block"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={beforeAlt}
          className="w-full h-full object-cover block"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      </div>

      {/* Glass labels */}
      <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-medium tracking-wide bg-white/70 backdrop-blur-md text-[#1E1E1E] shadow-sm border border-white/60">
        Before
      </span>
      <span className="absolute top-3 right-3 px-3 py-1 rounded-full text-[11px] font-medium tracking-wide bg-[#1E1E1E]/70 backdrop-blur-md text-white shadow-sm border border-white/20">
        After
      </span>

      {/* Divider */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/90 shadow-[0_0_20px_rgba(59,130,246,0.4)] pointer-events-none"
        style={{ left: `calc(${pos}% - 0.5px)` }}
      />

      {/* Handle */}
      <button
        type="button"
        aria-label="Drag to compare"
        onMouseDown={(e) => {
          e.preventDefault();
          dragging.current = true;
        }}
        onTouchStart={() => (dragging.current = true)}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white shadow-[0_8px_28px_rgba(30,30,30,0.25)] border border-white flex items-center justify-center cursor-ew-resize transition-transform duration-200 hover:scale-110 active:scale-95"
        style={{ left: `${pos}%` }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M9 6L3 12L9 18" stroke="#3B82F6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 6L21 12L15 18" stroke="#3B82F6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

export default BeforeAfterCompare;
